import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { THEME, gradients, shadows } from '../constants/theme';
import { supabase } from '../lib/supabase';
import {
  CurrencyDollarIcon,
  DocumentTextIcon,
  UserGroupIcon,
  ArrowTrendingUpIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import { useAnalytics } from '../hooks/useAnalytics';

const StatCard = ({ title, value, icon: Icon, trend, color }) => (
  <div 
    className="bg-white rounded-xl p-6 flex items-center"
    style={{ boxShadow: shadows.md }}
  >
    <div className="flex-1">
      <p className="text-sm text-gray-500">{title}</p>
      <p className="mt-2 text-3xl font-semibold" style={{ color: THEME.text }}>
        {value}
      </p>
      {trend && (
        <p className="mt-2 text-sm" style={{ color: trend >= 0 ? THEME.success : THEME.error }}>
          {trend >= 0 ? '↑' : '↓'} {Math.abs(trend)}% from last month
        </p>
      )}
    </div>
    <div 
      className="w-12 h-12 rounded-full flex items-center justify-center"
      style={{ backgroundColor: `${color}15`, color: color }}
    >
      <Icon className="w-6 h-6" />
    </div>
  </div>
);

const RecentActivity = ({ type, title, amount, date, status }) => (
  <div className="flex items-center py-3">
    <div 
      className="w-10 h-10 rounded-full flex items-center justify-center mr-4"
      style={{ backgroundColor: `${THEME.primary}15` }}
    >
      {type === 'invoice' && <CurrencyDollarIcon className="w-5 h-5" style={{ color: THEME.primary }} />}
      {type === 'quote' && <DocumentTextIcon className="w-5 h-5" style={{ color: THEME.primary }} />}
    </div>
    <div className="flex-1">
      <p className="font-medium" style={{ color: THEME.text }}>{title}</p>
      <p className="text-sm" style={{ color: THEME.subtext }}>{date}</p>
    </div>
    <div className="text-right">
      <p className="font-medium" style={{ color: THEME.text }}>{amount}</p>
      <p 
        className="text-sm capitalize"
        style={{ color: 
          status === 'paid' ? THEME.success :
          status === 'pending' ? THEME.warning :
          THEME.error 
        }}
      >
        {status}
      </p>
    </div>
  </div>
);

export default function Dashboard() {
  const { logEvent } = useAnalytics();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalRevenue: 0,
    totalQuotes: 0,
    activeClients: 0,
    monthlyGrowth: 0,
  });
  const [recentActivity, setRecentActivity] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        logEvent('Dashboard', 'Data Load', 'Initial Load');
        const { data: { user } } = await supabase.auth.getUser();
        
        // Get current month's start and end dates
        const now = new Date();
        const currentMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
        const currentMonthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        
        // Get last month's start and end dates
        const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);

        // Fetch current month's invoices
        const { data: currentMonthInvoices } = await supabase
          .from('invoices')
          .select('*, clients(*)')
          .eq('user_id', user.id)
          .gte('invoice_date', currentMonthStart.toISOString())
          .lte('invoice_date', currentMonthEnd.toISOString());

        // Fetch last month's invoices
        const { data: lastMonthInvoices } = await supabase
          .from('invoices')
          .select('*, clients(*)')
          .eq('user_id', user.id)
          .gte('invoice_date', lastMonthStart.toISOString())
          .lte('invoice_date', lastMonthEnd.toISOString());

        // Fetch recent invoices for activity feed
        const { data: recentInvoices } = await supabase
          .from('invoices')
          .select('*, clients(*)')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false })
          .limit(5);

        // Fetch quotes
        const { data: quotes } = await supabase
          .from('quotes')
          .select('*, clients(*)')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false })
          .limit(5);

        // Calculate stats
        const currentMonthRevenue = currentMonthInvoices?.reduce((sum, inv) => sum + (inv.total_amount || 0), 0) || 0;
        const lastMonthRevenue = lastMonthInvoices?.reduce((sum, inv) => sum + (inv.total_amount || 0), 0) || 0;
        const totalRevenue = recentInvoices?.reduce((sum, inv) => sum + (inv.total_amount || 0), 0) || 0;
        
        // Calculate monthly growth percentage
        const monthlyGrowth = lastMonthRevenue === 0 
          ? currentMonthRevenue > 0 ? 100 : 0
          : ((currentMonthRevenue - lastMonthRevenue) / lastMonthRevenue) * 100;

        const activeClients = new Set([
          ...(recentInvoices?.map(inv => inv.client_id) || []),
          ...(quotes?.map(q => q.client_id) || [])
        ]).size;

        setStats({
          totalRevenue,
          totalQuotes: quotes?.length || 0,
          activeClients,
          monthlyGrowth,
        });

        // Combine and sort recent activity
        const activity = [
          ...(recentInvoices?.map(inv => ({
            type: 'invoice',
            title: `Invoice #${inv.invoice_number}`,
            amount: `$${inv.total_amount}`,
            date: new Date(inv.created_at).toLocaleDateString(),
            status: inv.status.toLowerCase(),
            client: inv.clients?.client_name
          })) || []),
          ...(quotes?.map(quote => ({
            type: 'quote',
            title: `Quote #${quote.quote_number}`,
            amount: `$${quote.total}`,
            date: new Date(quote.created_at).toLocaleDateString(),
            status: quote.status.toLowerCase(),
            client: quote.clients?.client_name
          })) || [])
        ].sort((a, b) => new Date(b.date) - new Date(a.date)).slice(0, 5);

        setRecentActivity(activity);
      } catch (error) {
        logEvent('Dashboard', 'Data Load Error', error.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleTabChange = (tab) => {
    logEvent('Dashboard', 'Tab Change', tab);
    // ... existing tab change code ...
  };

  const handleFilterChange = (filter) => {
    logEvent('Dashboard', 'Filter Change', filter);
    // ... existing filter code ...
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-t-transparent" style={{ borderColor: `${THEME.primary} transparent` }} />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-2xl font-bold" style={{ color: THEME.text }}>Dashboard</h1>
        <p style={{ color: THEME.subtext }}>Welcome back! Here's what's happening with your business.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          title="Total Revenue"
          value={`$${stats.totalRevenue.toLocaleString()}`}
          icon={CurrencyDollarIcon}
          trend={stats.monthlyGrowth}
          color={THEME.success}
        />
        <StatCard
          title="Active Quotes"
          value={stats.totalQuotes}
          icon={DocumentTextIcon}
          color={THEME.primary}
        />
        <StatCard
          title="Active Clients"
          value={stats.activeClients}
          icon={UserGroupIcon}
          color={THEME.info}
        />
        <StatCard
          title="Monthly Growth"
          value={`${stats.monthlyGrowth}%`}
          icon={ArrowTrendingUpIcon}
          color={THEME.warning}
        />
      </div>

      <div className="bg-white rounded-xl p-6" style={{ boxShadow: shadows.md }}>
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <ClockIcon className="w-5 h-5 mr-2" style={{ color: THEME.primary }} />
            <h2 className="text-lg font-semibold" style={{ color: THEME.text }}>Recent Activity</h2>
          </div>
          <Link 
            to="/invoices" 
            className="text-sm font-medium hover:underline"
            style={{ color: THEME.primary }}
          >
            View all
          </Link>
        </div>

        <div className="divide-y">
          {recentActivity.map((activity, index) => (
            <RecentActivity key={index} {...activity} />
          ))}
        </div>
      </div>
    </div>
  );
} 